import { useReducer } from "react";
import { useNavigate } from "react-router-dom";

import { flattenObject, showToast } from "utils/utility";
import { useAffiliateAccountsData } from "libs/data-access";

import { IAffiliatePaymentRequest } from "./types";

type PaymentRequestDetails = IAffiliatePaymentRequest["details"];
type PaymentRequestErrors = IAffiliatePaymentRequest["errors"];

type ActionTypes =
    | {
        action: "UPDATE_LOADING";
        payload: boolean;
    }
    | {
        action: "UPDATE_DETAILS";
        payload: {
            field: keyof PaymentRequestDetails;
            value: PaymentRequestDetails[keyof PaymentRequestDetails];
        };
    }
    | {
        action: "UPDATE_ALL_DETAILS";
        payload: PaymentRequestDetails;
    }
    | {
        action: "UPDATE_ERROR";
        payload: {
            field: keyof PaymentRequestErrors;
            value: PaymentRequestErrors[keyof PaymentRequestErrors];
        };
    }
    | {
        action: "UPDATE_ALL_ERRORS";
        payload: PaymentRequestErrors;
    };

export default function useCreateNewPaymentRequest() {

    const navigate = useNavigate();

    const { postAffiliateAccountsData } = useAffiliateAccountsData();

    const DEPOSIT_TYPE_OPTIONS = [{
        label: "Cash",
        value: "CASH"
    },
    {
        label: "Cheque",
        value: "CHEQUE"
    },
    {
        label: "Inter Bank Transfer",
        value: "INTER_BANK_TRANSFER"
    }];

    const reducer = (
        state: IAffiliatePaymentRequest,
        action: ActionTypes
    ): IAffiliatePaymentRequest => {
        switch (action.action) {
            case "UPDATE_LOADING":
                return {
                    ...state,
                    isLoading: action.payload
                };

            case "UPDATE_DETAILS":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        [action.payload.field]: action.payload.value,
                    },
                    errors: {
                        ...state.errors,
                        [action.payload.field]: undefined
                    }
                };

            case "UPDATE_ALL_DETAILS":
                return {
                    ...state,
                    details: action.payload,
                    errors: {
                        depositType: undefined,
                        depositAmount: undefined,
                        dateOfDeposit: undefined,
                        beneficiaryBank: undefined,
                        beneficiaryName: undefined,
                        chequeDdNumber: undefined,
                        chequeDdIssueDate: undefined,
                        depositorBankAccountNumber: undefined,
                        depositorBankName: undefined,
                        depositorBankBranch: undefined
                    }
                };

            case "UPDATE_ERROR":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        [action.payload.field]: action.payload.value,
                    },
                };

            case "UPDATE_ALL_ERRORS":
                return {
                    ...state,
                    errors: action.payload,
                };

            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(reducer, {
        isLoading: false,
        details: {
            depositType: "CASH",
            depositAmount: 0,
            dateOfDeposit: null,
            beneficiaryBank: null,
            beneficiaryName: null,
            chequeDdNumber: null,
            chequeDdIssueDate: null,
            depositorBankAccountNumber: null,
            depositorBankName: null,
            depositorBankBranch: null
        },
        rules: {
            depositType: "YES",
            depositAmount: "YES",
            dateOfDeposit: "YES",
            beneficiaryBank: "NO",
            beneficiaryName: "NO",
            chequeDdNumber: "NO",
            chequeDdIssueDate: "NO",
            depositorBankAccountNumber: "NO",
            depositorBankName: "NO",
            depositorBankBranch: "NO"
        },
        errors: {
            depositType: undefined,
            depositAmount: undefined,
            dateOfDeposit: undefined,
            beneficiaryBank: undefined,
            beneficiaryName: undefined,
            chequeDdNumber: undefined,
            chequeDdIssueDate: undefined,
            depositorBankAccountNumber: undefined,
            depositorBankName: undefined,
            depositorBankBranch: undefined
        }
    });


    // HANDLER FUNCTIONS
    const validate = () => {

        const errors: PaymentRequestErrors = {
            depositType: (
                !state.details.depositType
                &&
                state.rules.depositType === "YES"
            ) ? "Deposit type is mandatory" : undefined,
            depositAmount: (
                !state.details.depositAmount
                &&
                state.rules.depositAmount === "YES"
            ) ? "Deposit amount is mandatory" : undefined,
            dateOfDeposit: (
                !state.details.dateOfDeposit
                &&
                state.rules.dateOfDeposit === "YES"
            ) ? "Date of deposit is mandatory" : undefined,
            beneficiaryBank: (
                !state.details.beneficiaryBank
                &&
                state.rules.beneficiaryBank === "YES"
            ) ? "Beneficiary bank is mandatory" : undefined,
            beneficiaryName: (
                !state.details.beneficiaryName
                &&
                state.rules.beneficiaryName === "YES"
            ) ? "Beneficiary name is mandatory" : undefined,
            chequeDdNumber: (
                !state.details.chequeDdNumber
                &&
                state.rules.chequeDdNumber === "YES"
            ) ? "Cheque/DD number is mandatory" : undefined,
            chequeDdIssueDate: (
                !state.details.chequeDdIssueDate
                &&
                state.rules.chequeDdIssueDate === "YES"
            ) ? "Cheque/DD issue date is mandatory" : undefined,
            depositorBankAccountNumber: (
                !state.details.depositorBankAccountNumber
                &&
                state.rules.depositorBankAccountNumber === "YES"
            ) ? "Depositor bank account number is mandatory" : undefined,
            depositorBankName: (
                !state.details.depositorBankName
                &&
                state.rules.depositorBankName === "YES"
            ) ? "Depositor bank name is mandatory" : undefined,
            depositorBankBranch: (
                !state.details.depositorBankBranch
                &&
                state.rules.depositorBankBranch === "YES"
            ) ? "Depositor bank branch is mandatory" : undefined
        };

        dispatch({ action: "UPDATE_ALL_ERRORS", payload: errors });

        let isValid = Object.values(flattenObject(errors)).every(val => !val);

        // console.log("errors: ", errors);
        // console.log("isValid: ", isValid);

        return isValid;

    };


    // API CALLS
    const saveRequest = async () => {

        const isValid = validate();

        if (!isValid) return;

        dispatch({ action: "UPDATE_LOADING", payload: true });

        const payload: PaymentRequestDetails = {
            depositType: state.details.depositType,
            depositAmount: state.details.depositAmount,
            dateOfDeposit: state.details.dateOfDeposit,
            beneficiaryBank: state.details.beneficiaryBank,
            beneficiaryName: state.details.beneficiaryName,
            chequeDdNumber: state.details.chequeDdNumber,
            chequeDdIssueDate: state.details.chequeDdIssueDate,
            depositorBankAccountNumber: state.details.depositorBankAccountNumber,
            depositorBankName: state.details.depositorBankName,
            depositorBankBranch: state.details.depositorBankBranch
        };

        // console.log("Payload for creating new request: ", payload);

        const response = await postAffiliateAccountsData("", payload);

        if (response.isSuccess) {
            // console.log("Response for creating new request: ", response.data);
            navigate(-1);
        } else {
            console.log("Error while saving data: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving data",
            });
        };

        dispatch({ action: "UPDATE_LOADING", payload: false });

    };


    return {
        navigate,
        DEPOSIT_TYPE_OPTIONS,
        state,
        dispatch,
        saveRequest
    };

}